import { useCallback } from 'react';
import { useMemo } from 'react';
import { StylesConfig } from 'react-select';
import OriginalSelect from 'react-select';
import { GroupBase } from 'react-select';
import { Props } from 'react-select';
import {
  components as originalComponents,
  DropdownIndicatorProps,
} from 'react-select';

const OriginalDropdownIndicator = originalComponents.DropdownIndicator;

export interface ExtraProps {
  selectRef?: any;
  appearance?: 'default' | 'compact';
}

interface StyleState {
  isDisabled: boolean;
}

const themes = {
  default: {
    container: (state: StyleState) => ({
      width: '100%',
    }),
    control: (state: StyleState) => ({
      minHeight: 48,
      borderRadius: 8,
      fontSize: 16,
      fontWeight: 500,
      ...(state.isDisabled && {
        backgroundColor: '#f7f7f7',
      }),
    }),
    valueContainer: (state: StyleState) => ({
      padding: '2px 14px',
    }),
    dropdownIndicator: (state: StyleState) => ({
      padding: '8px 16px',
    }),
    placeholder: (state: StyleState) => ({
      color: '#a0a4b5',
    }),
    singleValue: (state: StyleState) => ({
      ...(state.isDisabled && {
        color: '#828282',
      }),
    }),
  },
  compact: {
    container: (state: StyleState) => ({
      fontFamily: 'Roboto, sans-serif',
      width: '100%',
    }),
    control: (state: StyleState) => ({
      minHeight: 40,
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 400,
      ...(state.isDisabled && {
        backgroundColor: '#f7f7f7',
      }),
    }),
    valueContainer: (state: StyleState) => ({
      padding: '2px 14px',
    }),
    dropdownIndicator: (state: StyleState) => ({
      padding: '8px 16px',
    }),
    placeholder: (state: StyleState) => ({
      color: '#a0a4b5',
    }),
    singleValue: (state: StyleState) => ({
      ...(state.isDisabled && {
        color: '#828282',
      }),
    }),
  },
};

export const Select = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  styles,
  selectRef,
  appearance = 'default',
  components,
  ...props
}: Props<Option, IsMulti, Group> & ExtraProps) => {
  const customTheme = themes[appearance];
  const customStyles = useMemo<StylesConfig<Option, IsMulti, Group>>(
    () => ({
      ...styles,
      container: (provided, state) => ({
        ...provided,
        ...styles?.container?.(provided, state),
        ...customTheme.container(state),
      }),
      control: (provided, state) => ({
        ...provided,
        ...styles?.control?.(provided, state),
        ...customTheme.control(state),
      }),
      valueContainer: (provided, state) => ({
        ...provided,
        ...styles?.valueContainer?.(provided, state),
        ...customTheme.valueContainer(state),
      }),
      dropdownIndicator: (provided, state) => ({
        ...provided,
        ...styles?.dropdownIndicator?.(provided, state),
        ...customTheme.dropdownIndicator(state),
      }),
      placeholder: (provided, state) => ({
        ...provided,
        ...styles?.placeholder?.(provided, state),
        ...customTheme.placeholder(state),
      }),
      singleValue: (provided, state) => ({
        ...provided,
        ...styles?.singleValue?.(provided, state),
        ...customTheme.singleValue(state),
      }),
    }),
    [customTheme, styles],
  );

  const DropdownIndicator = useMemo(
    () => (props: DropdownIndicatorProps<Option, IsMulti, Group>) => {
      return (
        <OriginalDropdownIndicator {...props}>
          <svg
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L7 7L13 1"
              stroke="#333333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </OriginalDropdownIndicator>
      );
    },
    [],
  );

  const customComponents = useMemo(
    () => ({
      IndicatorSeparator: null,
      DropdownIndicator,
      ...components,
    }),
    [DropdownIndicator, components],
  );

  const theme = useCallback(
    (theme: any) => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: '#2d9cdb',
        primary75: '#5cb3e6',
        primary50: '#90cdf0',
        primary25: '#c8e8fa',
        neutral20: '#e0e0e0',
      },
    }),
    [],
  );

  return (
    <OriginalSelect
      {...props}
      theme={theme}
      styles={customStyles}
      components={customComponents}
      ref={selectRef}
    />
  );
};
